import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, InputAdornment, IconButton, Alert, Link } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

const Auth = () => {
    const [isRegister, setIsRegister] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    const handleLogin = (e) => {
        e.preventDefault();
        if (email && password) {
            setError('');
            setSuccess('Connection successful!');
            localStorage.setItem('isUser', 'true');
            setTimeout(() => navigate('/'), 1000);
        } else {
            setError('Please fill in all fields.');
        }
    };

    const handleRegister = (e) => {
        e.preventDefault();
        if (!email || !password || !confirmPassword) {
            setError('Please fill in all fields.');
            setSuccess('');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            setSuccess('');
            return;
        }
        setError('');
        setSuccess('Registration successful! You can now log in.');
        setIsRegister(false);
        setEmail('');
        setPassword('');
        setConfirmPassword('');
    };

    return (
        <Box sx={{ minHeight: '100vh', width: '100%', flex: 1, background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
            <Header />
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                <Paper elevation={8} sx={{
                    p: 5,
                    borderRadius: 4,
                    minWidth: 340,
                    maxWidth: 380,
                    width: '100%',
                    background: 'rgba(255,255,255,0.92)',
                    boxShadow: '0 8px 32px rgba(255,107,107,0.18)',
                }}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        {isRegister ? (
                            <PersonAddIcon sx={{ fontSize: 48, color: '#FF6B6B', mb: 1 }} />
                        ) : (
                            <LoginIcon sx={{ fontSize: 48, color: '#FF6B6B', mb: 1 }} />
                        )}
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            {isRegister ? 'Registration' : 'Login'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isRegister ? 'Create an account to access your space' : 'Login or create an account to access your space'}
                        </Typography>
                    </Box>
                    {isRegister ? (
                        <form onSubmit={handleRegister}>
                            <TextField
                                label="Email"
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                fullWidth
                                required
                                sx={{
                                    mb: 3,
                                    background: 'rgba(255,255,255,0.7)',
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#FF6B6B',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#E53E3E',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#E53E3E',
                                            boxShadow: '0 0 0 2px #FF6B6B33',
                                        },
                                    },
                                }}
                            />
                            <TextField
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                fullWidth
                                required
                                sx={{
                                    mb: 2,
                                    background: 'rgba(255,255,255,0.7)',
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#FF6B6B',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#E53E3E',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#E53E3E',
                                            boxShadow: '0 0 0 2px #FF6B6B33',
                                        },
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <TextField
                                label="Confirm Password"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                fullWidth
                                required
                                sx={{
                                    mb: 2,
                                    background: 'rgba(255,255,255,0.7)',
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#FF6B6B',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#E53E3E',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#E53E3E',
                                            boxShadow: '0 0 0 2px #FF6B6B33',
                                        },
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                size="large"
                                sx={{
                                    background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)',
                                    color: 'white',
                                    fontWeight: 700,
                                    borderRadius: 3,
                                    py: 1.5,
                                    mt: 1,
                                    boxShadow: '0 4px 16px rgba(255,107,107,0.10)',
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #E53E3E 0%, #FF6B6B 100%)',
                                    },
                                }}
                                startIcon={<PersonAddIcon />}
                            >
                                Register
                            </Button>
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                                <Link component="button" variant="body2" sx={{ color: '#FF6B6B', fontWeight: 600 }} onClick={() => { setIsRegister(false); setError(''); setSuccess(''); }}>
                                    Already have an account? Login
                                </Link>
                            </Box>
                        </form>
                    ) : (
                        <form onSubmit={handleLogin}>
                            <TextField
                                label="Email"
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                fullWidth
                                required
                                sx={{
                                    mb: 3,
                                    background: 'rgba(255,255,255,0.7)',
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#FF6B6B',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#E53E3E',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#E53E3E',
                                            boxShadow: '0 0 0 2px #FF6B6B33',
                                        },
                                    },
                                }}
                            />
                            <TextField
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                fullWidth
                                required
                                sx={{
                                    mb: 2,
                                    background: 'rgba(255,255,255,0.7)',
                                    borderRadius: 2,
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#FF6B6B',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#E53E3E',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#E53E3E',
                                            boxShadow: '0 0 0 2px #FF6B6B33',
                                        },
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                size="large"
                                sx={{
                                    background: 'linear-gradient(135deg, #FF6B6B 0%, #E53E3E 100%)',
                                    color: 'white',
                                    fontWeight: 700,
                                    borderRadius: 3,
                                    py: 1.5,
                                    mt: 1,
                                    boxShadow: '0 4px 16px rgba(255,107,107,0.10)',
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #E53E3E 0%, #FF6B6B 100%)',
                                    },
                                }}
                                startIcon={<LoginIcon />}
                            >
                                Login
                            </Button>
                            <Button
                                variant="outlined"
                                fullWidth
                                sx={{ mt: 2, borderColor: '#FF6B6B', color: '#FF6B6B', fontWeight: 700, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#E53E3E', background: 'rgba(255,107,107,0.05)', color: '#E53E3E' } }}
                                onClick={() => { setIsRegister(true); setError(''); setSuccess(''); }}
                            >
                                Create an account
                            </Button>
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                                <Link component="button" variant="body2" sx={{ color: '#FF6B6B', fontWeight: 600 }} onClick={() => navigate('/login')}>
                                    Login as administrator
                                </Link>
                            </Box>
                        </form>
                    )}
                </Paper>
            </Box>
            <Footer />
        </Box>
    );
};

export default Auth; 